export interface DropdownElements {
    container: HTMLElement;           // .dropdown element
    input: HTMLInputElement;          // .dropdown-input element
    options: HTMLElement;             // .options container
    optionItems: NodeListOf<HTMLElement> | HTMLElement[];
}

export interface DropdownPositionContext {
    /** Window inner height (use formDataService.getWindowInnerHeight() for iframe, window.innerHeight for web component) */
    viewportHeight: number;
    /** Frame top offset for iframe positioning (0 for web component) */
    frameTop?: number;
    /** Form container rect for boundary calculations */
    formContainerRect?: DOMRect;
}

export interface DropdownCallbacks {
    onSelect?: (value: string) => void;
    onChange?: () => void;
}

const MAX_DROPDOWN_HEIGHT = 400;
const DROPDOWN_PADDING = 8;

export function toggleDropdown(container: HTMLElement, forceState?: boolean): boolean {
    const isActive = forceState ?? !container.classList.contains('active');
    container.classList.toggle('active', isActive);
    return isActive;
}

export function isDropdownOpen(container: HTMLElement): boolean {
    return container.classList.contains('active');
}

export function adjustDropdownPosition(
    elements: DropdownElements,
    context: DropdownPositionContext
): 'top' | 'bottom' {
    const { container, options } = elements;
    const { viewportHeight, frameTop = 0, formContainerRect } = context;

    const dropdownRect = container.getBoundingClientRect();

    // Calculate available space
    let spaceBelow: number;
    let spaceAbove: number;

    if (formContainerRect) {
        // More precise calculation with form container bounds
        spaceBelow = Math.min(
            formContainerRect.bottom - dropdownRect.bottom,
            viewportHeight - dropdownRect.bottom - frameTop
        );
        spaceAbove = dropdownRect.top - Math.max(formContainerRect.top, frameTop);
    } else {
        // Simple calculation
        spaceBelow = viewportHeight - dropdownRect.bottom;
        spaceAbove = dropdownRect.top;
    }

    // Get options height (temporarily show if hidden)
    const wasHidden = options.style.display === 'none';
    if (wasHidden) {
        options.style.visibility = 'hidden';
        options.style.display = 'block';
    }
    const optionsHeight = options.scrollHeight;
    if (wasHidden) {
        options.style.display = '';
        options.style.visibility = '';
    }

    // Determine direction
    const renderBelow = spaceBelow >= optionsHeight || spaceBelow >= spaceAbove;

    if (renderBelow) {
        options.style.top = '100%';
        options.style.bottom = 'unset';
        options.style.maxHeight = `${Math.min(spaceBelow - DROPDOWN_PADDING, MAX_DROPDOWN_HEIGHT)}px`;
        return 'bottom';
    } else {
        options.style.bottom = '100%';
        options.style.top = 'unset';
        options.style.maxHeight = `${Math.min(spaceAbove - DROPDOWN_PADDING, MAX_DROPDOWN_HEIGHT)}px`;
        return 'top';
    }
}

export function selectOption(
    elements: DropdownElements,
    optionIndex: number,
    callbacks?: DropdownCallbacks
): void {
    const { container, input, optionItems } = elements;
    const items = Array.from(optionItems);

    if (optionIndex < 0 || optionIndex >= items.length) {
        return;
    }

    const option = items[optionIndex];
    const value = option.textContent || '';

    input.value = value;
    toggleDropdown(container, false);
    container.focus();

    // Dispatch native change event
    input.dispatchEvent(new Event('change', { bubbles: true }));

    callbacks?.onSelect?.(value);
    callbacks?.onChange?.();
}

export function highlightOption(
    optionItems: NodeListOf<HTMLElement> | HTMLElement[],
    index: number,
    highlightClass = 'highlighted'
): void {
    const items = Array.from(optionItems);
    items.forEach((option, i) => {
        if (i === index) {
            option.classList.add(highlightClass);
            option.scrollIntoView({ block: 'nearest' });
        } else {
            option.classList.remove(highlightClass);
        }
    });
}

export function handleDropdownKeyboard(
    event: KeyboardEvent,
    elements: DropdownElements,
    currentIndex: number,
    callbacks?: DropdownCallbacks
): number {
    const { container, optionItems } = elements;
    const items = Array.from(optionItems);
    const isActive = isDropdownOpen(container);

    let newIndex = currentIndex;

    switch (event.key) {
        case ' ':
        case 'Enter':
            event.preventDefault();
            if (!isActive) {
                toggleDropdown(container, true);
                newIndex = 0;
            } else if (currentIndex >= 0 && currentIndex < items.length) {
                selectOption(elements, currentIndex, callbacks);
                newIndex = -1;
            }
            break;

        case 'ArrowDown':
            event.preventDefault();
            if (!isActive) {
                toggleDropdown(container, true);
                newIndex = 0;
            } else {
                newIndex = Math.min(currentIndex + 1, items.length - 1);
            }
            break;

        case 'ArrowUp':
            event.preventDefault();
            if (isActive) {
                newIndex = Math.max(currentIndex - 1, 0);
            }
            break;

        case 'Escape':
            if (isActive) {
                event.preventDefault();
                toggleDropdown(container, false);
                container.focus();
                newIndex = -1;
            }
            break;

        case 'Tab':
            if (isActive) {
                toggleDropdown(container, false);
                newIndex = -1;
            }
            break;
    }

    return newIndex;
}

export function setupOptionsClick(
    elements: DropdownElements,
    callbacks?: DropdownCallbacks
): void {
    const { container, input, options } = elements;

    options.addEventListener('click', (event: Event) => {
        const target = event.target as HTMLElement;
        if (!target.classList.contains('option')) return;

        const value = target.textContent || '';
        input.value = value;
        toggleDropdown(container, false);
        container.focus();

        input.dispatchEvent(new Event('change', { bubbles: true }));

        callbacks?.onSelect?.(value);
        callbacks?.onChange?.();

        event.stopPropagation();
    });
}

export function setupDropdownTabulation(dropdownElement: HTMLElement): void {
    const dropdown = dropdownElement.querySelector<HTMLElement>('.dropdown');
    if (dropdown) {
        dropdown.tabIndex = 0;
    }

    const button = dropdownElement.querySelector<HTMLElement>('button');
    if (button) {
        button.tabIndex = -1;
    }

    const input = dropdownElement.querySelector<HTMLInputElement>('input');
    if (input) {
        input.tabIndex = -1;
    }
}

export function getDropdownElements(fieldElement: HTMLElement): DropdownElements | null {
    const container = fieldElement.querySelector<HTMLElement>('.dropdown');
    const input = fieldElement.querySelector<HTMLInputElement>('.dropdown-input, .input');
    const options = fieldElement.querySelector<HTMLElement>('.options');

    if (!container || !input || !options) return null;

    const optionItems = options.querySelectorAll<HTMLElement>('.option');

    return { container, input, options, optionItems };
}
